// 3rd party
import * as React from 'react';
import { Link } from 'react-router-dom';

// Bernie
import { AnalyticsStore, RouteStore } from 'bernie-core';

// Shared UI
import { LocalizedText } from '@shared-ui/localization-context';

// UI Toolkit
import { UitkSecondaryButton, UitkTertiaryButton } from 'uitk-react-button';
import { UitkCard, UitkCardContentSection } from 'uitk-react-cards';
import { UitkLayoutFlex, UitkLayoutFlexItem } from 'uitk-react-layout-flex';
import { UitkSpacing } from 'uitk-react-spacing';
import { UitkHeading, UitkText } from 'uitk-react-text';

// Components
import { UnsupportedLanguageDialog } from 'components/products/unsupported-language-dialog';

// Models
import { Product, Program } from 'models';

// Utils
import { getProductAnalyticsMicroMessage, getProgramAnalyticsMicroMessage } from '../../util/analytics';
import { isLocaleLanguage } from '../../util/locales';

interface ProductCardProps {
  analytics?: AnalyticsStore;
  product: Product;
  program?: Program;
  programStatus?: string;
  routeStore?: RouteStore;
  locale: string;
  isPending?: boolean;
  isPreview?: boolean;
  showActions?: boolean;
}

const ProductCard: React.FunctionComponent<ProductCardProps> = ({
  analytics,
  product,
  program,
  programStatus,
  routeStore,
  locale,
  isPending,
  isPreview,
  showActions,
}) => {
  const isUnsupportedLanguage = !product.isLocalized && !isLocaleLanguage(locale, 'en');
  const unsupportedLanguageDialogId = `unsupportedLanguage-${product.name}`;

  function handleLinkButtonClick(event: React.MouseEvent<HTMLButtonElement>) {
    if (isUnsupportedLanguage) {
      event.preventDefault();

      routeStore.updateSearch({ pwaDialog: unsupportedLanguageDialogId });
    }

    // TODO: Check current analytics tracking for Unsupported Language dialog cases
    if (typeof analytics?.trackEvent === 'function') {
      analytics.trackEvent('affhub_product.selected', 'AffHub Product Selected', null, null, true, [
        getProgramAnalyticsMicroMessage(program, programStatus),
        getProductAnalyticsMicroMessage(product),
      ]);
    }
  }

  function renderActionCTAs() {
    return (
      <UitkLayoutFlex alignItems="center" justifyContent="start">
        {product.link && (
          <UitkLayoutFlexItem>
            {product.isExternal ? (
              <UitkSecondaryButton tag="a" href={product.link} target="_blank" onClick={handleLinkButtonClick}>
                <LocalizedText
                  message={
                    product.name === 'covid19'
                      ? 'products.covid19.cta.text'
                      : `products.cta.${isPreview ? 'preview' : 'open'}`
                  }
                />
              </UitkSecondaryButton>
            ) : (
              <Link className="a--no-hover" to={product.link}>
                <UitkSecondaryButton onClick={handleLinkButtonClick}>
                  <LocalizedText
                    message={
                      product.name === 'covid19'
                        ? 'products.covid19.cta.text'
                        : `products.cta.${isPreview ? 'preview' : 'open'}`
                    }
                  />
                </UitkSecondaryButton>
              </Link>
            )}
          </UitkLayoutFlexItem>
        )}

        {product.guideLink && (
          <UitkLayoutFlexItem>
            <UitkTertiaryButton tag="a" href={product.guideLink} target="_blank">
              <LocalizedText message="products.guide.text" />
            </UitkTertiaryButton>
          </UitkLayoutFlexItem>
        )}
      </UitkLayoutFlex>
    );
  }

  return (
    <UitkCard className="Product" padded={true}>
      <UitkCardContentSection>
        <UitkLayoutFlex alignItems="start">
          <UitkLayoutFlexItem>
            <UitkSpacing margin={{ blockstart: 'one', inlineend: 'six' }}>
              <i className="Product__icon" style={{ backgroundImage: `url(../../images/icons/${product.icon}.svg)` }} />
            </UitkSpacing>
          </UitkLayoutFlexItem>

          <UitkLayoutFlexItem>
            <div>
              <div>
                <UitkHeading tag="h3" size={5}>
                  <LocalizedText message={`products.${product.name}.title`} />
                </UitkHeading>

                <UitkSpacing margin={{ blockstart: 'one' }}>
                  <UitkText size={300}>
                    <LocalizedText message={`products.${product.name}.content`} />
                  </UitkText>
                </UitkSpacing>
              </div>

              {isPending && (
                <UitkSpacing margin={{ blockstart: 'four' }}>
                  <UitkText size={300}>
                    <LocalizedText message="products.pending.application.under.review" />
                  </UitkText>
                </UitkSpacing>
              )}

              {showActions && product.isLive && (product.link || product.guideLink) && (
                <UitkSpacing margin={{ blockstart: 'four' }}>
                  <div>{renderActionCTAs()}</div>
                </UitkSpacing>
              )}

              {!product.isLive && (
                <UitkSpacing margin={{ blockstart: 'four' }}>
                  <UitkHeading size={6}>
                    <LocalizedText message="common.coming_soon" />
                  </UitkHeading>
                </UitkSpacing>
              )}
            </div>
          </UitkLayoutFlexItem>
        </UitkLayoutFlex>

        {isUnsupportedLanguage && (
          <UnsupportedLanguageDialog dialogId={unsupportedLanguageDialogId} product={product} routeStore={routeStore} />
        )}
      </UitkCardContentSection>
    </UitkCard>
  );
};

export { ProductCard };
